<script>
import { mapState, mapActions } from 'pinia'
import { Log } from '@artup/utilities'
import { useClientStore, useRouterStore, FormMixin } from '@artup/client'
import { Account, Countries, Avatar } from '@artup/model'
import { useAccountsStore } from '../../../store'
import AccountsForm from '../../../components/accounts-form.vue'

export default {
  mixins: [
    FormMixin
  ],

  components: {
    'artup-accounts-view': AccountsForm
  },

  data () {
    return {
      // Indicates whether we're in the initial loading phase
      isInitialized: false,
      // Activation request details
      request: null,
      // Activated account
      account: null,
      // Entered account data
      firstName: '',
      lastName: '',
      organization: '',
      password: '',
      countryCode: '',
      accountProfile: '',
      // Flag indicating whether password should be displayed in clear text
      revealPassword: false,
      // Countries to select from
      countries: [],
      // Indicates whether activation has been attempted
      activationAttempted: false,
      // Activation error
      activationError: null
    }
  },

  computed: {
    ...mapState(useRouterStore, [
      'currentRoute'
    ]),

    ...mapState(useClientStore, [
      'isDevelopmentEnvironment',
      'isMobilePhone',
      'configuration',
      'isBusy',
      'ticker'
    ]),

    // Indicates that UI should be locked due to the ongoing action
    isLocked () {
      return this.isBusy
    },

    // Indicates that activation request has been retrieved and it is still valid
    hasValidRequest () {
      const { request } = this
      return request != null && !request.hasExpired
    },

    // Indicates that activation request has been retrieved but it has expired
    requestExpired () {
      const { request } = this
      return request != null && request.hasExpired
    },

    // Indicates that activation request has already been used
    requestUsed () {
      const { request } = this
      return request != null && request.hasBeenUsed
    },

    // Activation token
    token () {
      return this.currentRoute.params.token
    },

    // Security profiles to select from
    profiles () {
      return this.request.data?.securityProfiles || []
    },

    // Selected profile
    profile () {
      return this.profiles.find(p => p.name === this.accountProfile)
    },

    // Indicates that the selected profile represents a human
    isHuman () {
      const { profile } = this
      return !profile || profile.isHuman
    },

    // Indicates whether the user has filled in all the required data
    // and we can proceed with account creation
    canSave () {
      const { isHuman, password, firstName, lastName, organization, countryCode, accountProfile } = this
      if (Boolean(password.trim()) &&
        Boolean(countryCode.trim()) &&
        Boolean(accountProfile.trim())) {
        return isHuman
          ? Boolean(firstName.trim()) && Boolean(lastName.trim())
          : Boolean(organization.trim())
      }
      return false

    },

    // Indicates that activation has failed
    activationFailed () {
      return this.activationAttempted && !this.account
    },

    // Indicates that activation has succeeded
    activationSucceeded () {
      return this.activationAttempted && this.account && this.request.hasBeenUsed
    },

    // Dialog title
    title () {
      const { activationAttempted, isBusy, activationSucceeded, requestUsed } = this
      if (requestUsed) {
        return 'Account is active'
      }
      if (activationAttempted) {
        if (isBusy) return 'Account Activation'
        if (activationSucceeded) return 'Activation Completed'
        return 'Activation Failed'
      }
      return 'Activate Account'
    },

    // Dialog subtitle
    subtitle () {
      const { hasValidRequest, activationAttempted, requestUsed, requestExpired } = this
      if (activationAttempted) return
      if (requestUsed) return
      if (hasValidRequest) {
        return 'Please enter your data'
      }
      if (requestExpired) {
        return 'Activation was not completed in time'
      }
    }
  },

  methods: {
    ...mapActions(useClientStore, [
      'busy',
      'done'
    ]),

    ...mapActions(useAccountsStore, [
      'getSecurityRequest',
      'activateAccount',
    ]),

    async populate ({ token } = {}) {
      this.countries = Countries.All
      this.request = null
      this.account = null

      if (token) {
        const request = await this.getSecurityRequest({ token, geo: true })
        if (request) {
          const account = new Account(request.account)
          this.request = request
          this.account = account
          if (request.data?.address) {
            this.countryCode = request.data.address.countryCode
          }
        }
      }
    },

    // Filter function for filtering options in the country selector
    filterCountries (value = '', update) {
      update(() => {
        if (value === '') {
          this.countries = Countries.All
        } else {
          const text = value.toLowerCase()
          this.countries = Countries.All.filter(o => o.name.toLowerCase().includes(text))
        }
      })
    },

    // Activates the account with the provided data
    async activate (reCaptchaToken) {
      const { canSave, token, account, password, firstName, lastName, organization, countryCode, profile } = this
      if (!canSave) return

      this.activationAttempted = true
      try {
        const avatar = new Avatar({
          isDefault: true,
          name: profile.description,
          securityProfileName: profile.name,
          firstName: profile.isHuman ? firstName : null,
          lastName: profile.isHuman ? lastName : null,
          organization: profile.isHuman ? null : organization
        })
        account.assign({ password, countryCode })
        account.avatars = [avatar]
        await this.busy({ message: `Creating new ARTup account ${account.name} ...` })
        this.account = await this.activateAccount({ token, account, sendEmail: true, reCaptchaToken })
        if (this.account !== null) {
          this.request.used = new Date()
        }
        await this.done()

      } catch (error) {
        this.activationError = error.message
        this.account = null
        Log.exception(error)

      } finally {
        await this.done()
      }
    }
  },

  async beforeRouteUpdate (to) {
    await this.populate({ token: to.params.token })
  },

  async created () {
    await this.populate({ token: this.token })
    this.isInitialized = true
  }
}

</script>

<template>
  <artup-accounts-view v-cloak v-if="isInitialized" class="activate" :title="title"
    :subtitle="subtitle">

    <template #content>
      <!-- Activation request exists -->
      <q-form ref="form" class="form" v-if="hasValidRequest && !activationAttempted">
        <div class="fields">
          <div>
            <q-input class="input-name q-mt-sm" outlined :model-value="account.name"
              readonly autocomplete="username" maxlength="45">
            </q-input>
          </div>

          <div class="q-mt-sm">
            <q-input class="input-password q-mt-sm" outlined label="Password"
              v-model="password" autocomplete="current-password" maxlength="45"
              :type="revealPassword ? 'text' : 'password'">

              <template v-slot:append>
                <q-icon name="visibility" class="button-reveal-password cursor-pointer"
                  :disable="isLocked" @click="revealPassword = !revealPassword">
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="q-mt-md profiles">
            <div class="q-ml-sm q-mt-sm">
              <label>
                Which option describes you best?
              </label>
              <q-list class="profile-list q-mt-sm q-mr-sm q-mb-md">
                <q-item v-for="profile in profiles" clickable
                  :active="profile.name === accountProfile" active-class="bg-indigo-2 text-indigo-8"
                  @click="accountProfile = profile.name">
                  <q-item-section side>
                    <q-icon :name="profile.icon" color="indigo-5" size="md" class="q-pa-xs">
                    </q-icon>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ profile.details || profile.description }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>

          <div class="q-mt-sm" :class="{ row: !isMobilePhone }" v-if="isHuman">
            <div>
              <q-input class="input-name q-mt-sm" label="First Name" outlined
                v-model="firstName" maxlength="45">
              </q-input>
            </div>
            <div :class="{ 'q-ml-md': !isMobilePhone, 'q-mt-md': isMobilePhone }">
              <q-input class="input-name q-mt-sm" label="Last Name" outlined
                v-model="lastName" maxlength="45">
              </q-input>
            </div>
          </div>
          <div v-else class="q-mt-sm">
            <div>
              <q-input class="input-name q-mt-sm" label="Institution or company name"
                outlined v-model="organization" maxlength="200">
              </q-input>
            </div>
          </div>

          <div class="q-mt-sm">
            <q-select outlined class="q-mt-sm" label="Your country" v-model="countryCode"
              :options="countries" emit-value map-options options-cover option-value="code"
              option-label="name" use-input debounce="500" fill-input hide-selected autocomplete
              @filter="filterCountries">
            </q-select>
          </div>

        </div>

        <div class="buttons q-mt-lg">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Continue"
            unelevated rounded no-caps @click="activate()" :disable="!canSave">
          </q-btn>
        </div>
      </q-form>

      <!-- Activation in progress -->
      <div class="form q-mt-sm" v-else-if="activationAttempted && isBusy">
        <div class="fields text-center">
          Activating account {{ account.name }}.
          <br>
          Please wait ...
        </div>
      </div>

      <!-- Activation succeeded -->
      <div class="form q-mt-sm" v-else-if="activationSucceeded">
        <div class="fields text-center">
          Account {{ account.name }} has been created.
          <br>
          You can now log in to ARTup community!
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Continue to login"
            unelevated rounded no-caps :to="{ name: 'login', query: { name: account.name } }">
          </q-btn>
        </div>
      </div>

      <!-- Activation failed -->
      <div class="form q-mt-sm" v-else-if="hasValidRequest && activationFailed">
        <div class="fields text-center">
          We apologize for the inconvenience.
          <br>
          Please try again at later time.
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md"
            label="Try another sign up" unelevated rounded no-caps :to="{ name: 'signup' }">
          </q-btn>
        </div>
      </div>

      <!-- Activation request not found -->
      <div class="form q-mt-sm" v-else>
        <div class="fields column items-center">
          <div v-if="requestExpired" class="column items-center justify-center text-orange-9">
            <label>
              Your account request has already expired.
            </label>
            <label>
              Click the button below to sign up again.
            </label>
          </div>
          <div v-else class="column items-center justify-center text-orange-9">
            <label>
              Your account request has already expired.
            </label>
            <label>
              Click the button below to join the ARTup Community.
            </label>
          </div>
        </div>

        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Sign Up" unelevated
            rounded no-caps :to="{ name: 'signup' }">
          </q-btn>
        </div>
      </div>
    </template>

  </artup-accounts-view>
</template>

<style scoped lang="scss">
.profiles {
  border: solid #0000001f 1px;
  border-radius: 4px;

  label {
    color: #00000099;
    font-size: 13px;
    margin-left: 4px;
  }

  .profile-list {
    border-radius: 4px !important;
  }
}
</style>